'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { Plus, Search, Eye, ArrowLeft, TrendingUp, TrendingDown, CheckCircle2, Clock, XCircle, RotateCcw, Download } from 'lucide-react';
import { Card } from '@/components/ui/Cards';
import {
  MOVEMENT_TYPE_LABELS, MOVEMENT_TYPE_COLORS, MOVEMENT_STATUS_LABELS,
  type BirdMovement, type BirdMovementType, type BirdMovementStatus
} from '../../components/birdRecordsData';
import { useBirdMovements, useUpdateMovementStatus } from '@/hooks/useBirdMovements';
import { toast } from 'sonner';

// ─── Badges ───────────────────────────────────────────────────────────────────

function MovementTypeBadge({ type }: { type: BirdMovementType }) {
  const color = MOVEMENT_TYPE_COLORS[type];
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
      style={{ backgroundColor: `${color}18`, color }}
    >
      {MOVEMENT_TYPE_LABELS[type]}
    </span>
  );
}

function StatusBadge({ status }: { status: BirdMovementStatus }) {
  const map: Record<BirdMovementStatus, string> = {
    draft: 'bg-gray-100 text-gray-600',
    pending_approval: 'bg-amber-100 text-amber-700',
    approved: 'bg-blue-100 text-blue-700',
    posted: 'bg-green-100 text-green-700',
    reversed: 'bg-purple-100 text-purple-700',
    rejected: 'bg-red-100 text-red-700',
    cancelled: 'bg-gray-100 text-gray-500',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${map[status]}`}>
      {MOVEMENT_STATUS_LABELS[status]}
    </span>
  );
}

function DirectionBadge({ direction }: { direction: string }) {
  if (direction === 'in') return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700">
      <TrendingUp size={10} />IN
    </span>
  );
  if (direction === 'out') return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600">
      <TrendingDown size={10} />OUT
    </span>
  );
  return <span className="text-xs font-medium text-muted-foreground">—</span>;
}

// ─── Movement Detail Drawer ───────────────────────────────────────────────────

function MovementDetailDrawer({ movement, onClose }: { movement: BirdMovement; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-2xl bg-card rounded-2xl border border-border/60 shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-border/60">
          <div>
            <h3 className="text-sm font-semibold text-foreground">{movement.movementNumber}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{movement.movementDate} · {movement.farmName}</p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={movement.status} />
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground transition-colors">
              <XCircle size={14} />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Movement Info */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { label: 'Movement Type', value: MOVEMENT_TYPE_LABELS[movement.movementType] },
              { label: 'Direction', value: movement.direction.toUpperCase() },
              { label: 'Quantity', value: movement.quantity.toLocaleString() },
              { label: 'Flock', value: `${movement.flockNumber} — ${movement.flockName}` },
              { label: 'Farm', value: movement.farmName },
              { label: 'House', value: movement.houseName ?? '—' },
              { label: 'Bird Type', value: movement.birdType },
              { label: 'Breed', value: movement.breed },
              { label: 'Reference', value: movement.referenceId ?? '—' },
            ].map((item) => (
              <div key={item.label}>
                <p className="text-xs text-muted-foreground">{item.label}</p>
                <p className="text-xs font-medium text-foreground mt-0.5">{item.value}</p>
              </div>
            ))}
          </div>

          {/* Population Impact */}
          <div className="p-3 rounded-xl bg-muted/20 border border-border/60">
            <p className="text-xs font-semibold text-foreground mb-2">Population Impact</p>
            <div className="flex items-center gap-4 text-xs">
              <div>
                <p className="text-muted-foreground">Before</p>
                <p className="font-tabular font-semibold text-foreground">{movement.populationBefore?.toLocaleString() ?? '—'}</p>
              </div>
              <div className="text-muted-foreground">→</div>
              <div>
                <p className="text-muted-foreground">Change</p>
                <p className={`font-tabular font-semibold ${movement.direction === 'in' ? 'text-green-600' : 'text-red-600'}`}>
                  {movement.direction === 'in' ? '+' : '-'}{movement.quantity.toLocaleString()}
                </p>
              </div>
              <div className="text-muted-foreground">→</div>
              <div>
                <p className="text-muted-foreground">After</p>
                <p className="font-tabular font-bold text-foreground">{movement.populationAfter?.toLocaleString() ?? '—'}</p>
              </div>
            </div>
          </div>

          {/* Reason & Notes */}
          {movement.reason && (
            <div>
              <p className="text-xs text-muted-foreground">Reason</p>
              <p className="text-xs text-foreground mt-0.5">{movement.reason}</p>
            </div>
          )}

          {/* Audit Trail */}
          <div>
            <p className="text-xs font-semibold text-foreground mb-2">Audit Trail</p>
            <div className="space-y-2">
              {[
                { label: 'Created', by: movement.recordedBy, at: movement.createdAt, icon: Clock },
                ...(movement.approvedBy ? [{ label: 'Approved', by: movement.approvedBy, at: movement.updatedAt, icon: CheckCircle2 }] : []),
                ...(movement.status === 'posted' ? [{ label: 'Posted to Ledger', by: 'System', at: movement.updatedAt, icon: CheckCircle2 }] : []),
              ].map((step) => (
                <div key={step.label} className="flex items-center gap-2 text-xs">
                  <step.icon size={12} className="text-green-600 flex-shrink-0" />
                  <span className="text-muted-foreground">{step.label}</span>
                  <span className="text-foreground font-medium">{step.by}</span>
                  <span className="text-muted-foreground ml-auto">{new Date(step.at).toLocaleDateString('en-GB')}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Ledger ──────────────────────────────────────────────────────────────

export default function BirdMovementsContent() {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [directionFilter, setDirectionFilter] = useState('all');
  const [selectedMovement, setSelectedMovement] = useState<BirdMovement | null>(null);

  // ── Live data via TanStack Query ──────────────────────────────────────────
  const { data: allMovements = [], isLoading } = useBirdMovements();
  const updateStatus = useUpdateMovementStatus();

  const filtered = useMemo(() => {
    return allMovements.filter((m) => {
      const matchSearch = !search ||
        m.movementNumber.toLowerCase().includes(search.toLowerCase()) ||
        m.flockNumber.toLowerCase().includes(search.toLowerCase()) ||
        m.flockName.toLowerCase().includes(search.toLowerCase()) ||
        m.farmName.toLowerCase().includes(search.toLowerCase());
      const matchType = typeFilter === 'all' || m.movementType === typeFilter;
      const matchStatus = statusFilter === 'all' || m.status === statusFilter;
      const matchDir = directionFilter === 'all' || m.direction === directionFilter;
      return matchSearch && matchType && matchStatus && matchDir;
    }).sort((a, b) => new Date(b.movementDate).getTime() - new Date(a.movementDate).getTime());
  }, [allMovements, search, typeFilter, statusFilter, directionFilter]);

  function handleReverse(movement: BirdMovement) {
    updateStatus.mutate(
      { id: movement.id, status: 'reversed', updatedBy: 'Current User' },
      {
        onSuccess: () => toast.success(`Movement ${movement.movementNumber} reversed`),
        onError: () => toast.error('Failed to reverse movement'),
      },
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Link href="/bird-records" className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground transition-colors">
            <ArrowLeft size={14} />
          </Link>
          <div>
            <h1 className="text-lg font-semibold text-foreground">Bird Movement Ledger</h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              Authoritative record of all bird population changes — {allMovements.length} movements
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-border/60 rounded-lg bg-card hover:bg-muted/50 transition-colors">
            <Download size={12} />
            Export
          </button>
          <Link
            href="/bird-records/movements/new"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Plus size={12} />
            Record Movement
          </Link>
        </div>
      </div>

      {/* Search & Filters */}
      <Card padding="sm">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by movement #, flock, farm..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-border/60 rounded-lg bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="text-xs border border-border/60 rounded-lg px-3 py-1.5 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
          >
            <option value="all">All Types</option>
            {Object.entries(MOVEMENT_TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-xs border border-border/60 rounded-lg px-3 py-1.5 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
          >
            <option value="all">All Statuses</option>
            {Object.entries(MOVEMENT_STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <select
            value={directionFilter}
            onChange={(e) => setDirectionFilter(e.target.value)}
            className="text-xs border border-border/60 rounded-lg px-3 py-1.5 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
          >
            <option value="all">All Directions</option>
            <option value="in">IN</option>
            <option value="out">OUT</option>
            <option value="neutral">NEUTRAL</option>
          </select>
        </div>
      </Card>

      {/* Ledger Table */}
      <Card padding="sm">
        {isLoading ? (
          <div className="py-12 text-center text-xs text-muted-foreground">Loading movements…</div>
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border/60">
                {['Movement #', 'Date', 'Type', 'Farm / House', 'Flock', 'Qty', 'Direction', 'Pop. Before', 'Pop. After', 'Reference', 'Recorded By', 'Status', 'Actions'].map((h) => (
                  <th key={h} className="text-left py-2 px-2 text-muted-foreground font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={13} className="py-8 text-center text-muted-foreground text-xs">
                    No movements match your filters
                  </td>
                </tr>
              ) : (
                filtered.map((m) => (
                  <tr key={m.id} className="border-b border-border/40 hover:bg-muted/30 transition-colors">
                    <td className="py-2 px-2 font-medium text-primary whitespace-nowrap">{m.movementNumber}</td>
                    <td className="py-2 px-2 text-muted-foreground whitespace-nowrap">{m.movementDate}</td>
                    <td className="py-2 px-2"><MovementTypeBadge type={m.movementType} /></td>
                    <td className="py-2 px-2 text-muted-foreground whitespace-nowrap">
                      <div className="text-foreground">{m.farmName}</div>
                      <div className="text-muted-foreground/70">{m.houseName}</div>
                    </td>
                    <td className="py-2 px-2 text-foreground whitespace-nowrap">{m.flockNumber}</td>
                    <td className="py-2 px-2 font-tabular font-semibold text-foreground">{m.quantity.toLocaleString()}</td>
                    <td className="py-2 px-2"><DirectionBadge direction={m.direction} /></td>
                    <td className="py-2 px-2 font-tabular text-muted-foreground">{m.populationBefore?.toLocaleString() ?? '—'}</td>
                    <td className="py-2 px-2 font-tabular font-semibold text-foreground">{m.populationAfter?.toLocaleString() ?? '—'}</td>
                    <td className="py-2 px-2 text-muted-foreground whitespace-nowrap">{m.referenceId ?? '—'}</td>
                    <td className="py-2 px-2 text-muted-foreground whitespace-nowrap">{m.recordedBy}</td>
                    <td className="py-2 px-2"><StatusBadge status={m.status} /></td>
                    <td className="py-2 px-2">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setSelectedMovement(m)}
                          className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors"
                          title="View Details"
                        >
                          <Eye size={12} />
                        </button>
                        {m.status === 'posted' && (
                          <button
                            onClick={() => handleReverse(m)}
                            disabled={updateStatus.isPending}
                            className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                            title="Reverse"
                          >
                            <RotateCcw size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        )}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/60">
          <p className="text-xs text-muted-foreground">Showing {filtered.length} of {allMovements.length} movements</p>
          <div className="flex items-center gap-1">
            <button className="px-2 py-1 text-xs border border-border/60 rounded-lg hover:bg-muted/50 transition-colors text-muted-foreground">Prev</button>
            <span className="px-2 py-1 text-xs bg-primary text-primary-foreground rounded-lg">1</span>
            <button className="px-2 py-1 text-xs border border-border/60 rounded-lg hover:bg-muted/50 transition-colors text-muted-foreground">Next</button>
          </div>
        </div>
      </Card>

      {/* Flock Ledger (BR-2026-001) */}
      <Card padding="sm">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Flock Population History — BR-2026-001</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Batch A1 — Cobb 500 · Idudi Poultry Farm · House A1</p>
          </div>
        </div>
        <div className="space-y-1">
          {allMovements
            .filter((m) => m.flockNumber === 'BR-2026-001' && m.status === 'posted')
            .sort((a, b) => new Date(a.movementDate).getTime() - new Date(b.movementDate).getTime())
            .map((m, i, arr) => {
              const balance = arr.slice(0, i + 1).reduce((acc, mv) => {
                if (mv.direction === 'in') return acc + mv.quantity;
                if (mv.direction === 'out') return acc - mv.quantity;
                return acc;
              }, 0);
              const color = MOVEMENT_TYPE_COLORS[m.movementType];
              return (
                <div key={m.id} className="flex items-center gap-3 py-1.5 px-2 rounded-lg hover:bg-muted/30 transition-colors text-xs">
                  <span className="text-muted-foreground w-12 flex-shrink-0">{new Date(m.movementDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>
                  <span className="font-medium text-muted-foreground w-24 flex-shrink-0">{m.movementNumber}</span>
                  <span className="flex-1 text-foreground">{MOVEMENT_TYPE_LABELS[m.movementType]}</span>
                  <span className="font-tabular font-semibold w-16 text-right flex-shrink-0" style={{ color }}>
                    {m.direction === 'in' ? '+' : '-'}{m.quantity.toLocaleString()}
                  </span>
                  <span className="font-tabular font-bold w-24 text-right flex-shrink-0 text-foreground">Balance: {balance.toLocaleString()}</span>
                </div>
              );
            })}
        </div>
      </Card>

      {/* Detail Drawer */}
      {selectedMovement && (
        <MovementDetailDrawer movement={selectedMovement} onClose={() => setSelectedMovement(null)} />
      )}
    </div>
  );
}
